import { Button } from '@/components/ui/button';
import { UserPlus, UserMinus, Loader2, UserCheck } from 'lucide-react';
import { useUserFollows, useClubFollows } from '@/hooks/useFollows';
import { useIsFollowedBy } from '@/hooks/useFriends';
import { useAuth } from '@/hooks/useAuth';

interface FollowUserButtonProps {
  userId: string;
  variant?: 'default' | 'outline' | 'ghost' | 'gradient';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
  className?: string;
}

export function FollowUserButton({ userId, variant = 'outline', size = 'sm', showLabel = true, className }: FollowUserButtonProps) {
  const { profile } = useAuth();
  const { isFollowingUser, followUser, unfollowUser } = useUserFollows();
  const { data: isFollowedByTarget } = useIsFollowedBy(userId);
  
  const isFollowing = isFollowingUser(userId);
  const isLoading = followUser.isPending || unfollowUser.isPending;
  const isOwnProfile = profile?.id === userId;
  const isFriend = isFollowing && isFollowedByTarget;

  if (isOwnProfile || !profile) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isFollowing) {
      unfollowUser.mutate(userId);
    } else {
      followUser.mutate(userId);
    }
  };

  // Show "Bạn bè" badge when already friends
  if (isFriend) {
    return (
      <Button
        variant="ghost"
        size={size}
        className={`text-accent pointer-events-none ${className}`}
        disabled
      >
        <UserCheck className="w-4 h-4 mr-1" />
        <span className="text-xs">Bạn bè</span>
      </Button>
    );
  }

  // Hide button when following but not mutual
  if (isFollowing) return null;

  // Show "Kết bạn" if they follow us, otherwise "Theo dõi"
  const label = isFollowedByTarget ? 'Kết bạn' : 'Theo dõi';
  const buttonVariant = isFollowedByTarget ? 'gradient' as any : variant;

  return (
    <Button
      variant={buttonVariant}
      size={size}
      onClick={handleClick}
      disabled={isLoading}
      className={className}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <span className="text-xs">{label}</span>
      )}
    </Button>
  );
}

interface FollowClubButtonProps {
  clubId: string;
  variant?: 'default' | 'outline' | 'ghost' | 'gradient';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
  className?: string;
}

export function FollowClubButton({ clubId, variant = 'outline', size = 'sm', showLabel = true, className }: FollowClubButtonProps) {
  const { profile } = useAuth();
  const { isFollowingClub, followClub, unfollowClub } = useClubFollows();
  
  const isFollowing = isFollowingClub(clubId);
  const isLoading = followClub.isPending || unfollowClub.isPending;

  if (!profile) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isFollowing) {
      unfollowClub.mutate(clubId);
    } else {
      followClub.mutate(clubId);
    }
  };

  // Hide button when already following
  if (isFollowing) return null;

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={isLoading}
      className={className}
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <span className="text-xs">Theo dõi</span>
      )}
    </Button>
  );
}
